import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdAdd, MdEdit, MdDelete, MdAccountBalance } from "react-icons/md";
import { createApi } from "../../../../core/api";

export const CompanyPackageItemBalanceCrud = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const [companyId, setCompanyId] = useState<number | "">("");
  const [selectedSubId, setSelectedSubId] = useState<number | "">("");
  const [companySubscriptions, setCompanySubscriptions] = useState<any[]>([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [packageItemId, setPackageItemId] = useState<number | "">("");
  const [balance, setBalance] = useState<number | "">("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, i] = await Promise.all([
        api.companyPackageItemBalance.getAll(),
        api.company.getAll(),
        api.packageItem.getAll(),
      ]);

      if (b?.ok) setData(b.data?.data ?? b.data ?? []);
      if (c?.ok) setCompanies(c.data?.data ?? c.data ?? []);
      if (i?.ok) setItems(i.data?.data ?? i.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Company Package Item Balance",
        items: [
          {
            label: "Company Package Item Balance",
            route: "/dashboard/company-package-item-balance",
          },
        ],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const handleCompanyChange = async (cid: number | "") => {
    setCompanyId(cid);
    setSelectedSubId("");
    setPackageItemId("");
    setCompanySubscriptions([]);

    if (!cid) return;

    setLoadingSubs(true);
    try {
      const res = await api.companyModuleSubscription.getDashbordById(Number(cid));
      const modules = res?.data?.data?.modules ?? res?.data?.modules ?? [];
      const subs = modules.map((m: any) => ({
        subscriptionId: m.companyModuleSubscriptionId ?? m.subscriptionId,
        label: `${m.moduleName ?? ""} — ${m.packageTitle ?? ""}`,
        items: (m.items ?? []).map((i: any) => ({
          id: i.packageItemId,
          code: i.packageItemCode,
          description: i.packageItemDescription,
        })),
      }));
      setCompanySubscriptions(subs);
    } catch (e) {
      console.error("Failed to load company subscriptions", e);
    } finally {
      setLoadingSubs(false);
    }
  };

  // Seçilen pakette tanımlı olan kalemler
  const packageItemsInSub = useMemo(() => {
    if (!selectedSubId) return [];
    const sub = companySubscriptions.find((s) => s.subscriptionId === Number(selectedSubId));
    return sub?.items ?? [];
  }, [selectedSubId, companySubscriptions]);

  // Seçilen pakette olmayan diğer tüm kalemler
  const otherItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    const inSubIds = new Set(packageItemsInSub.map((i: any) => i.id));
    return items.filter((i: any) => !inSubIds.has(i.id));
  }, [items, packageItemsInSub]);

  const openCreate = () => {
    setEditing(null);
    setCompanyId("");
    setSelectedSubId("");
    setCompanySubscriptions([]);
    setPackageItemId("");
    setBalance("");
    setError(null);
    setOpen(true);
  };

  const openEdit = async (row: any) => {
    setEditing(row);
    const cid = row.companyId ?? row.companyModuleSubscription?.companyId ?? "";
    const subId = row.companyModuleSubscriptionId ?? row.companyModuleSubscription?.id ?? "";
    setCompanyId(cid);
    setSelectedSubId(subId);
    setPackageItemId(row.packageItemId ?? "");
    setBalance(row.totalAmount ?? row.balance ?? "");
    setError(null);
    setOpen(true);

    if (cid) {
      setLoadingSubs(true);
      try {
        const res = await api.companyModuleSubscription.getDashbordById(Number(cid));
        const modules = res?.data?.data?.modules ?? res?.data?.modules ?? [];
        const subs = modules.map((m: any) => ({
          subscriptionId: m.companyModuleSubscriptionId ?? m.subscriptionId,
          label: `${m.moduleName ?? ""} — ${m.packageTitle ?? ""}`,
          items: (m.items ?? []).map((i: any) => ({
            id: i.packageItemId,
            code: i.packageItemCode,
            description: i.packageItemDescription,
          })),
        }));
        setCompanySubscriptions(subs);
      } catch (e) {
        console.error("Failed to load company subscriptions", e);
      } finally {
        setLoadingSubs(false);
      }
    }
  };

  const submit = async () => {
    if (!companyId || !selectedSubId || !packageItemId || balance === "" || saving) return;

    setSaving(true);
    setError(null);

    try {
      if (editing) {
        const r = await api.companyPackageItemBalance.update(editing.id, {
          companyId,
          companyModuleSubscriptionId: Number(selectedSubId),
          packageItemId,
          totalAmount: Number(balance),
          balance: Number(balance),
        });
        if (!r?.ok) throw new Error(r?.message || r?.errors?.[0] || "Update failed");
      } else {
        const r = await api.companyPackageItemBalance.create({
          companyId,
          companyModuleSubscriptionId: Number(selectedSubId),
          packageItemId,
          totalAmount: Number(balance),
          balance: Number(balance),
        });
        if (!r?.ok) throw new Error(r?.message || r?.errors?.[0] || "Create failed");
      }

      setOpen(false);
      load();
    } catch (e: any) {
      setError(e?.message || "Unexpected error occurred");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };

  const doDelete = async () => {
    if (!deleteId || deleting) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const r = await api.companyPackageItemBalance.delete(deleteId);
      if (!r?.ok) throw new Error(r?.message || "Delete failed");

      setDeleteId(null);
      load();
    } catch (e: any) {
      setDeleteError(e?.message || "Unexpected error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "company",
        title: "Company",
        minWidth: 260,
        accessor: (r: any) => {
          const cid = r.companyId ?? r.companyModuleSubscription?.companyId;
          const c = companies.find((x) => x.id === cid);
          return c?.companyName || c?.name || (cid ? `Company #${cid}` : "");
        },
        render: (_: any, v: string) => (
          <Badge label={v} color="#0EA5E9" bg="rgba(14,165,233,.15)" />
        ),
      },
      {
        key: "subscription",
        title: "Package / Module",
        minWidth: 240,
        accessor: (r: any) => {
          const mod = r.companyModuleSubscription?.module?.name || r.companyModuleSubscription?.moduleCode || "";
          const pkg = r.companyModuleSubscription?.package?.title || r.package?.title || "";
          if (mod && pkg) return `${mod} — ${pkg}`;
          if (pkg) return pkg;
          if (mod) return mod;
          return r.companyModuleSubscriptionId ? `Sub #${r.companyModuleSubscriptionId}` : "-";
        },
        render: (_: any, v: string) => (
          <Badge label={v} color="#8B5CF6" bg="rgba(139,92,246,.12)" />
        ),
      },
      {
        key: "item",
        title: "Package Item",
        minWidth: 260,
        accessor: (r: any) => {
          const item = items.find((x) => x.id === r.packageItemId);
          return item?.code
            ? `${item.code}${item.description ? ` (${item.description})` : ""}`
            : (item?.name || `Item #${r.packageItemId}`);
        },
        render: (_: any, v: string) => (
          <Badge
            icon={MdAccountBalance}
            label={v}
            color="#F59E0B"
            bg="rgba(245,158,11,.15)"
          />
        ),
      },
      {
        key: "balance",
        title: "Total / Extra",
        minWidth: 140,
        accessor: (r: any) => `${r.totalAmount ?? r.balance ?? 0}${r.extraAmount ? ` (+${r.extraAmount})` : ""}`,
      },
      {
        key: "usage",
        title: "Used / Remaining",
        minWidth: 160,
        accessor: (r: any) => {
          const total = (r.totalAmount ?? r.balance ?? 0) + (r.extraAmount ?? 0);
          const used = r.usedAmount ?? 0;
          const rem = r.remainingAmount ?? (total - used);
          return `${used} used / ${rem} left`;
        },
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 200,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                id: "edit",
                icon: MdEdit,
                color: "#2563EB",
                onClick: () => openEdit(row),
              },
              {
                id: "delete",
                icon: MdDelete,
                color: "#DC2626",
                onClick: () => confirmDelete(row.id),
              },
            ]}
          />
        ),
      },
    ],
    [companies, items]
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Company Package Item Balance</div>
          <div style={S.subTitle}>Manage company item balances</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Add Balance
        </Button>
      </div>

      <Table
        tableKey="company-package-item-balance"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>
          {editing ? "Update Balance" : "Add Balance"}
        </div>

        <div style={S.form}>
          <label style={S.label}>Company</label>
          <select
            value={companyId}
            onChange={(e) => handleCompanyChange(e.target.value ? Number(e.target.value) : "")}
            style={S.input}
            disabled={saving || !!editing}
          >
            <option value="">Select company</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName || c.name || `Company #${c.id}`}
              </option>
            ))}
          </select>

          <label style={S.label}>
            Package / Subscription {loadingSubs ? "(Loading...)" : ""}
          </label>
          <select
            value={selectedSubId}
            onChange={(e) => {
              setSelectedSubId(e.target.value ? Number(e.target.value) : "");
              setPackageItemId("");
            }}
            style={S.input}
            disabled={saving || !companyId || loadingSubs || !!editing}
          >
            <option value="">
              {!companyId
                ? "First select a company"
                : loadingSubs
                ? "Loading packages..."
                : companySubscriptions.length === 0
                ? "No active packages found for company"
                : "Select package / subscription"}
            </option>
            {companySubscriptions.map((s) => (
              <option key={s.subscriptionId} value={s.subscriptionId}>
                {s.label}
              </option>
            ))}
          </select>

          <label style={S.label}>Package Item (Kalem / Hak Türü)</label>
          <select
            value={packageItemId}
            onChange={(e) => setPackageItemId(e.target.value ? Number(e.target.value) : "")}
            style={S.input}
            disabled={saving || !selectedSubId}
          >
            <option value="">
              {!selectedSubId ? "First select a package" : "Select item"}
            </option>
            {packageItemsInSub.length > 0 && (
              <optgroup label="Pakete Tanımlı Kalemler">
                {packageItemsInSub.map((i: any) => (
                  <option key={`sub-${i.id}`} value={i.id}>
                    {i.code ? `${i.code}${i.description ? ` — ${i.description}` : ""}` : (i.name || `Item #${i.id}`)}
                  </option>
                ))}
              </optgroup>
            )}
            {otherItems.length > 0 && (
              <optgroup label="Diğer Tüm Hak Kalemleri (Ekstra)">
                {otherItems.map((i: any) => (
                  <option key={`all-${i.id}`} value={i.id}>
                    {i.code ? `${i.code}${i.description ? ` — ${i.description}` : ""}` : (i.name || `Item #${i.id}`)}
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          <label style={S.label}>Balance (Hak Miktarı)</label>
          <input
            type="number"
            value={balance}
            onChange={(e) => setBalance(Number(e.target.value))}
            style={S.input}
            disabled={saving || !packageItemId}
          />

          {error && <div style={S.error}>{error}</div>}
        </div>

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={saving}
            disabled={saving || !companyId || !selectedSubId || !packageItemId || balance === "" || Number(balance) <= 0}
          >
            Save
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!deleteId}
        onClose={() => (!deleting ? setDeleteId(null) : null)}
      >
        <div style={S.modalTitle}>Delete Balance</div>

        <div style={{ fontSize: 14, color: "#334155" }}>
          Are you sure you want to delete this balance?
        </div>

        {deleteError && <div style={S.error}>{deleteError}</div>}

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setDeleteId(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={doDelete}
            loading={deleting}
            disabled={deleting}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    position: "absolute",
    inset: 0,
    padding: 20,
    background: "#F8FAFC",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    color: "#0F172A",
  },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 14,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
